/* eslint-disable max-lines-per-function */
import { A } from '@adornis/base/env-info.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { BHDialogFreeFormMails } from '@adornis/bh-mails/client/bh-dialog-free-form-mails.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { removeParticipationsByUserAndInstitution } from '@adornis/buchungsheld-db/api/participation.js';
import { subscribeUserDataview, subscribeUserDataviewCount } from '@adornis/buchungsheld-db/api/user-data.js';
import { sendFreeFormMail } from '@adornis/buchungsheld-db/api/users.js';
import { AdminInstitutionViewSetting } from '@adornis/buchungsheld-db/entities/AdminInstitutionViewSetting.js';
import type { Appointment } from '@adornis/buchungsheld-db/entities/appointment.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import { InstitutionMailTemplate, MailTemplateType } from '@adornis/buchungsheld-db/entities/mail-templates.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { dataviewEmptyState } from '@adornis/buchungsheld-db/global-helpers.js';
import { getPluginForScope } from '@adornis/buchungsheld-db/plugins/plugin.js';
import { UserDataview } from '@adornis/buchungsheld-db/use-case-entities/UserDataview.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import '@adornis/buchungsheld-theme/components/tr-checkbox';
import '@adornis/buchungsheld-theme/components/tr-chip.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { obs } from '@adornis/chemistry/directives/observable.js';
import '@adornis/chemistry/elements/components/x-button.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { exportDataView } from '@adornis/data-view/db/export-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { downloadLink } from '@adornis/file-utils/client/helper-methods.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { consume } from '@lit/context';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { until } from 'lit/directives/until.js';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  merge,
  Subject,
  switchMap,
  timer,
} from 'rxjs';
import { BHPromptIkhExportPayments } from '../../prompts/bh-prompt-ikh-export-payments.js';
import { BHPromptSuspendUser } from '../../prompts/bh-prompt-suspend-user.js';
import { entityContextTokenProvider } from '../../util.js';

export const adminEinrichtungUserRouter = makeRouter(
  {
    root: {
      path: '/',
      metaData: {
        semesterSelect: true,
        breadcrumbs: ({ translate, params }) => [{ content: translate('counseling', 'participant'), active: true }],
      },
      render: params => {
        console.log('params users: ', params);
        return html`
          <bh-page-admin-einrichtung-users institution-id=${params.institutionID}></bh-page-admin-einrichtung-users>
        `;
      },
    },
    userDetail: {
      path: '/userDetail/:userID',
      render: params => html`
        <bh-page-admin-einrichtung-user-detail
          institution-id=${params.institutionID}
        ></bh-page-admin-einrichtung-user-detail>
      `,
    },
  },
  {
    fallback: () => html` <x-empty-placeholder .img=${'close'} .text=${'404'} no-wrapper></x-empty-placeholder> `,
  },
);

const getLsKey = (institutionID: string) => `extra_columns:${institutionID}:participants_export:v2`;

function getCachedExtraColumns(institutionID: string) {
  const lsKey = getLsKey(institutionID);
  const extraColumns = JSON.parse(localStorage.getItem(lsKey) ?? '[]');
  return extraColumns;
}

function setCachedExtraColumns(institutionID: string, columns: string[]) {
  const lsKey = getLsKey(institutionID);
  localStorage.setItem(lsKey, JSON.stringify(columns));
}

/**
 * @element bh-page-admin-einrichtung-users
 */
@customElement('bh-page-admin-einrichtung-users')
export class BHPageAdminEinrichtungUsers extends BuchungsheldLitElement {
  @property({ attribute: 'institution-id' }) institutionID = new RXController<string | null>(this, null);

  private readonly _selectedTimeframe = new BehaviorSubject<string | null>(null);

  @consume({
    context: entityContextTokenProvider.getContext(AdminInstitutionViewSetting),
    subscribe: true,
  })
  @state()
  protected readonly viewSettings?: {
    $instance: AdminInstitutionViewSetting & {
      institutionDetailsUsersFilterV1?: BHFilter;
    };
  };

  @state()
  private readonly fullOptions = new RXController(
    this,
    combineLatest([this._selectedTimeframe, this.institutionID.observable]).pipe(
      map(([timeframeID, institutionID]) => {
        if (!institutionID) return null;

        const filter = new BHFilter({
          institutionID: institutionID,
          timeframeID: timeframeID,
          searchFields: ['user.firstName', 'user.lastName', 'user.email'],
        });

        const savedAspects = this.viewSettings?.$instance?.institutionDetailsUsersFilterV1?.aspects;
        const savedSortFields = this.viewSettings?.$instance?.institutionDetailsUsersFilterV1?.sortingFields;
        const savedSortDirections = this.viewSettings?.$instance?.institutionDetailsUsersFilterV1?.sortingDirections;
        if (savedAspects && savedAspects.length > 0) filter.aspects = savedAspects as any;
        if (savedSortFields && savedSortFields.length > 0) filter.sortingFields = savedSortFields as any;
        if (savedSortDirections && savedSortDirections.length > 0)
          filter.sortingDirections = savedSortDirections as any;

        const view = new View({ filter });
        const extraColumns = getCachedExtraColumns(institutionID).map(fieldName => ({ fieldName }));

        const { dataViewOptions, filterOptions } = buildFullOptions(
          {
            class: UserDataview,
            operation: subscribeUserDataview,
            isEntryClickable: true,
            isSortable: true,
            isColumnSelectable: true,
            isColumnResizable: true,
            view,
            onViewChanged: view => {
              this.onViewUpdate(dataViewOptions, view);
              // TODO: save filter in onSortUpdate Event or somehow ignore dataview update
              // this.saveFilter(view.filter);
            },
            emptyState: () => dataviewEmptyState({}),
            fieldSelectionOptions: this.fieldSelectionOptions,
            defaultFieldMapping: this.dataviewFieldMapping as any,
            customColumns: [
              {
                customID: '_select',
                columnWidth: 'max-content',
                renderItem: entity => this.renderSlectionColumnCeckbox(entity),
                renderHeader: () => this.renderSlectionColumnCeckboxHeader(),
              },
              { fieldName: 'user.firstName' },
              { fieldName: 'user.lastName' },
              { fieldName: 'user.email' },
              {
                columnName: this._translation.translate('termin', 'plural'),
                fieldName: 'participations.product.name',
                columnWidth: '3fr',
                renderItem: entity => this.renderProductsColumn(entity),
              },

              {
                columnName: this._translation.translate('field', 'waitingListPlace'),
                fieldName: 'participations.waitingListPlace',
                selectionSet: { 'participations.product': 1 },
                columnWidth: '3fr',
                renderItem: entity => {
                  console.log({ entity });
                  const places = entity.participations
                    ?.map(p => {
                      console.log({ p });
                      if (!p.waitingListPlace) return null;
                      return html`<text>${p.product?.name}: ${p.waitingListPlace ?? '---'}</text><br />`;
                    })
                    .filter(isDefined);

                  if (places.length > 0) return places;
                  else return html`<text ${css({ textAlign: 'center' })}>---</text>`;
                },
              },

              ...extraColumns,

              {
                columnName: '',
                customID: '_options',
                columnWidth: 'max-content',
                selectionSet: UserDataview.neededGqlFields,
                isAlwaysVisible: true,
                renderItem: instance => this.renderOptionsColumn(instance),
              },
            ] as Array<ColumnDefinition<typeof UserDataview>>,
          },
          {
            fieldAspectGeneration: {
              shouldIncludeField: this.fieldSelectionOptions.shouldIncludeField,
            },
          },
        );

        return { dataViewOptions, filterOptions, view };
      }),
      filter(Boolean),
    ),
  );

  filterOptions$ = new Subject<BHFilter>();

  saveFilter = async (filter: any) => {
    if (!this.viewSettings?.$instance || !this.institutionID.value) return;
    const instance = this.viewSettings.$instance;
    instance.institutionDetailsUsersFilterV1 = filter;
    await instance.save();
  };

  @state() filter = new RXController(
    this,
    combineLatest([this.institutionID.observable]).pipe(
      filter(([institutionID]) => !!institutionID),
      map(
        ([institutionID]) =>
          new BHFilter({
            institutionID,
            aspects: [],
            searchFields: ['user.firstName', 'user.lastName', 'user.email'],
            search: '',
          }),
        new BHFilter({}),
      ),
    ),
  );

  memberCount = merge(this.filter.observable, this.filterOptions$).pipe(
    filter(Boolean),
    switchMap(filter => subscribeUserDataviewCount(filter)()),
    distinctUntilChanged(),
  );

  @state() private _selectedUsers = new RXController<string[] | 'all'>(this, []);

  override render() {
    if (!this.viewSettings?.$instance?.selectedTimeframeID) this._selectedTimeframe.next(null);
    else this._selectedTimeframe.next(this.viewSettings.$instance.selectedTimeframeID);

    const fullOptions = this.fullOptions.value;
    if (this.institutionID.isLoading || !this.institutionID.value || !fullOptions)
      return html`<x-loading-state></x-loading-state>`;

    const { dataViewOptions, filterOptions, view } = fullOptions;

    return html`
      <tr-flex space="md">
        Teilnehmer: ${obs(this.memberCount.pipe(map(count => html`<x-text> ${count} </x-text>`)))}

        <!-- import/export -->
        ${this.renderImportExportSection(dataViewOptions, view)}

        <x-filter-editor
          .options=${filterOptions}
          @filter-changed=${e => this.filterOptions$.next(e.detail.value)}
          @filter-apply=${e => this.saveFilter(e.detail)}
          @filter-reset=${() => this.saveFilter(null)}
        ></x-filter-editor>
        ${this._selectedUsers.value.length >= 1 || this._selectedUsers.value === 'all'
          ? html`
              <x-flex
                horizontal
                space-between
                ${acss({
                  position: 'sticky',
                  top: `${this.spacing.xl + this.spacing.md}px`,
                  padding: this.spacing.xs,
                  border: `2px solid ${this.colors.secondary.shade(300)}`,
                  borderRadius: this.spacing.sm,
                  background: this.colors.tone.snowWhite!.opacity(0.92),
                  margin: `0 -${this.spacing.xs + 2}px`,
                  zIndex: '9',

                  'tr-icon': {
                    marginLeft: `-${this.spacing.xxs}`,
                  },
                  '[info]': {
                    color: this.colors.secondary,
                  },
                })}
              >
                <x-flex horizontal start crossaxis-center space="xs">
                  <tr-button-small icon="person" .color=${this.colors.secondary.shade(300)} .action=${async () => null}>
                    <tr-text bold ${css({ marginLeft: `-${this.spacing.xs}` })}
                      >${this._selectedUsers.value === 'all'
                        ? until(firstValueFrom(this.memberCount), '...')
                        : this._selectedUsers.value.length}x</tr-text
                    >
                  </tr-button-small>

                  <tr-text bold info> markierte Anmeldungen </tr-text>
                </x-flex>

                <x-flex horizontal start crossaxis-center space="xs">
                  <tr-button-small
                    icon="mail"
                    .color=${this.colors.secondary}
                    .action=${async () => {
                      const mailAddresses = Array.isArray(this._selectedUsers.value)
                        ? await getByID(
                            User,
                            this._selectedUsers.value,
                          )({ email: 1 }).then(users => users.map(user => user.email))
                        : await firstValueFrom(
                            subscribeUserDataview(
                              0,
                              10000,
                              dataViewOptions.view.filter || new BHFilter({}),
                            )({ user: { email: 1 } }),
                          ).then(userObjs => userObjs.map(userObj => userObj.user.email));
                      if (mailAddresses.length === 10000) {
                        TrDialog.alert('Zu viele Nutzer gleichzeitig selektiert.');
                        throw new Error('Cannot select more than 10000 users at the same time');
                      }

                      console.log('hello?', mailAddresses, this._selectedUsers.value);

                      BHDialogFreeFormMails.showPopup({
                        props: {
                          institutionID: this.institutionID.value,
                          mailAddresses,
                          mailTemplateType: MailTemplateType.FREE_FORM,
                          mailTemplateConstructor: (doc: PartialEntityData<InstitutionMailTemplate>) =>
                            new InstitutionMailTemplate({ ...doc, institutionID: this.institutionID.value }),
                          mailSendOperation: sendFreeFormMail,
                        },
                      });
                    }}
                  >
                    <x-text> Mail senden </x-text>
                  </tr-button-small>
                  <tr-button-small
                    .color=${this.colors.secondary.shade(300)}
                    icon="clear"
                    .action=${async () => this._selectedUsers.next([])}
                  ></tr-button-small>
                </x-flex>
              </x-flex>
            `
          : nothing}

        <x-data-view .options=${dataViewOptions}></x-data-view>
      </tr-flex>
    `;
  }

  private renderImportExportSection(dataViewOptions, view) {
    const institutionID = this.institutionID.value;
    if (!institutionID) return;
    const stammdatenPlugin = getPluginForScope('registration::stammdaten', institutionID);

    const isIKH = stammdatenPlugin.id.includes('ikh') || stammdatenPlugin.id.includes('sd');
    return html`
      <x-flex space="md">
        <x-h3> Import / Export </x-h3>
        <x-flex horizontal space="md">
          <x-button mode="outline" icon="download" @click=${() => this.exportParticipations(dataViewOptions, view)}>
            ${this._translation.translate('imp_exp', 'exp_participations')}
          </x-button>

          ${(() => {
            if (isIKH)
              return html`
                <x-button mode="outline" icon="download" @click=${() => this.exportWorkshopsIkhInternalPayments()}>
                  ${this._translation.translate('imp_exp', 'exp_intern_payments')}
                </x-button>
              `;
          })()}
        </x-flex>
      </x-flex>
    `;
  }

  private async exportParticipations(dataViewOptions, view) {
    const institutionID = this.institutionID.value;
    if (!institutionID) return;

    // FIXME: this should not be necessary
    console.warn('this hack should be removed when the dataview is fixed');
    dataViewOptions.operation = 'subscribeUserDataview';
    exportDataView(dataViewOptions, { fileName: `teilnehmer-${institutionID}` }).catch(this.e);
  }

  private async exportWorkshopsIkhInternalPayments() {
    const institutionID = this.institutionID.value;
    if (!institutionID) return;

    const result = await BHPromptIkhExportPayments.showPopup<Maybe<Appointment>>({});
    if (!result) return;

    try {
      await XDialog.waitFor(
        Promise.all([
          Workshop.exportWorkshopIkhInternalPaymentDataCSV(institutionID, result.start, result.end)().then(fileName =>
            downloadLink(A.absoluteUrl(`/__export/download/${fileName}`), 'zahlungen.csv'),
          ),
          firstValueFrom(timer(500)),
        ]),
      );
    } catch (e) {
      await XDialog.alert(this._translation.translate('workshop', 'export_err'));
    }
  }

  // data-view options ------------------------------------------
  get fieldWhitelist() {
    return [
      'user',
      'user._id',
      'user.firstName',
      'user.lastName',
      'user.title',
      'user.email',
      'user.suspended',
      'userStammdaten',
      'participations',
      'participations.waitingListPlace',
      'participations.product',
      'participations.product.category',
      'participations.product.category.name',
      'participations.product.timeframe',
      'participations.product.timeframe.name',
      'participations.product.timeframe.start',
      'participations.product.timeframe.end',
    ];
  }

  get fieldSelectionOptions() {
    return {
      shouldIncludeField: (baseQLFieldDefinition, path, baseEntity) => {
        // only show show members that are relevant for the given institution
        if (baseEntity._class === 'UserStammdaten') {
          const institutionID = this.institutionID.value ?? '';
          const institutionStammdatenFields = UserStammdaten.getStammdatenFieldsForInstitution(institutionID);
          return institutionStammdatenFields.includes(baseQLFieldDefinition.name);
        }

        const fieldKey = path.join('.') + '.' + baseQLFieldDefinition.name;
        if (fieldKey === 'user.participations') return false;
        if (fieldKey === 'participations.user') return false;
        if (fieldKey === 'participations.userStammdaten') return false;
        if (fieldKey === 'participations.product.participations') return false;

        return this.fieldWhitelist.includes(fieldKey) || this.fieldWhitelist.includes(baseQLFieldDefinition.name);
      },
    };
  }

  onViewUpdate(dataViewOptions: DataViewOptions<any>, view: View<any>) {
    console.log('on view changed');
    const preExistingFields = dataViewOptions.customColumns!.map(col => col.fieldName).filter(isDefined);
    const cachedExtraFields = getCachedExtraColumns(this.institutionID.value ?? '');
    const selectionFields = view.configuredColumns!.map(col => col.fieldName).filter(isDefined);
    const newFields = selectionFields.filter(it => !preExistingFields.includes(it));
    const fieldsToBeRemoved = preExistingFields.filter(it => !selectionFields.includes(it));
    const newCachedExtraFields = cachedExtraFields.filter(it => !fieldsToBeRemoved.includes(it));
    for (const field of newFields) newCachedExtraFields.push(field);

    console.log({ newCachedExtraFields });
    setCachedExtraColumns(this.institutionID.value ?? '', newCachedExtraFields);

    view.configuredColumns?.sort((col1, col2) => {
      if (col1.customID === '_select') return -1;
      if (col1.customID === '_options') return 1;
      if (col2.customID === '_select') return 1;
      if (col2.customID === '_options') return -1;
      return 0;
    });
  }

  get dataviewFieldMapping() {
    return {
      // string matches currently everything except DateTime
      // TODO: extra type for enums would be nice
      ['string' as any]: value => {
        const enumTranslation = UserStammdaten.getEnumTranslation(this._translation, value);
        if (enumTranslation) return enumTranslation;

        return undefined; // use dataview-default
      },
    };
  }

  // data-view custom columns -----------------------------------------------------------
  renderSlectionColumnCeckbox(instance: UserDataview) {
    return obs(
      this._selectedUsers.observable.pipe(
        map(selectedUsers => {
          return selectedUsers === 'all' || selectedUsers.includes(instance.user._id);
        }),
        map(
          value => html`
            <tr-checkbox
              outlined
              boolean-only
              enableicon="adjust"
              disableicon
              @click=${(e: MouseEvent) => e.stopPropagation()}
              @value-picked=${(e: ValueEvent<boolean>) => {
                if (this._selectedUsers.value === 'all') return this._selectedUsers.next([instance.user._id]);
                if (e.detail.value === true && !this._selectedUsers.value.includes(instance.user._id))
                  return this._selectedUsers.next((this._selectedUsers.value as string[]).concat([instance.user._id]));
                if (e.detail.value === false && this._selectedUsers.value.includes(instance.user._id)) {
                  const index = this._selectedUsers.value.indexOf(instance.user._id);
                  if (index !== -1)
                    this._selectedUsers.next((this._selectedUsers.value as string[]).toSpliced(index, 1));
                  return;
                }
              }}
              .value=${value}
            ></tr-checkbox>
          `,
        ),
      ),
    );
  }

  renderSlectionColumnCeckboxHeader() {
    return obs(
      this._selectedUsers.observable.pipe(
        map(
          selectedUsers => html`
            <tr-checkbox
              outlined
              boolean-only
              enableicon="adjust"
              disableicon
              @click=${(e: MouseEvent) => e.stopPropagation()}
              @value-picked=${(e: ValueEvent<boolean>) => {
                if (e.detail.value === true) this._selectedUsers.next('all');
                if (e.detail.value === false) this._selectedUsers.next([]);
              }}
              .value=${selectedUsers === 'all'}
            ></tr-checkbox>
          `,
        ),
      ),
    );
  }

  renderProductsColumn(instance: UserDataview) {
    return html`
      <tr-flex horizontal wrap space="sm">
        ${instance.participations.map(participation => {
          return html` <tr-text> ${participation.product?.name ?? '?'} </tr-text> `;
        })}
      </tr-flex>
    `;
  }

  renderOptionsColumn(instance: UserDataview) {
    const isSuspended = !!instance.user.suspentions
      .map(suspention => suspention.institutionID)
      .includes(this.institutionID.value!);
    return html`
      <x-flex horizontal space="sm">
        <tr-button-small
          icon="edit"
          mode="outline"
          .color=${this.colors.tone.onyx}
          .action=${async e => {
            adminEinrichtungUserRouter.goTo('userDetail', {
              params: {
                userID: instance.user._id,
              },
            });
          }}
        >
          <tr-tooltip .text=${html` <x-text> ${this._translation.translate('user', 'details')} </x-text> `}>
          </tr-tooltip>
        </tr-button-small>

        <tr-button-small
          icon="email"
          mode="outline"
          .action=${() =>
            BHDialogFreeFormMails.showPopup({
              props: {
                institutionID: this.institutionID.value,
                mailAddresses: [instance.user.email],
                mailTemplateType: MailTemplateType.FREE_FORM,
                mailTemplateConstructor: (doc: PartialEntityData<InstitutionMailTemplate>) =>
                  new InstitutionMailTemplate({ ...doc, institutionID: this.institutionID.value }),
                mailSendOperation: sendFreeFormMail,
              },
            })}
        >
          <tr-tooltip .text=${html` <x-text>${this._translation.translate('user', 'write')}</x-text> `}></tr-tooltip>
        </tr-button-small>

        ${isSuspended
          ? html`
              <tr-button-small
                mode="outline"
                icon="check"
                .color=${this.colors.tone.success}
                .action=${async e => {
                  e.preventDefault();
                  e.stopPropagation();

                  if (
                    !(await XDialog.confirm(
                      this._translation.translate('user', 'share_user_with_institution') + ` (${instance.user.email})`,
                    ))
                  )
                    return;

                  if (!this.institutionID.value) return;

                  await XDialog.waitFor(
                    User.cancelSuspensionFromInstitution(instance.user._id, this.institutionID.value)(),
                    this._translation.translate('user', 'anull_suspension') as string,
                  );
                  this.requestUpdate();
                }}
              >
                <tr-tooltip
                  .text=${html` <x-text>${this._translation.translate('user', 'anull_suspension')}</x-text> `}
                >
                </tr-tooltip>
              </tr-button-small>
            `
          : html`
              <tr-button-small
                icon="block"
                .color=${this.colors.tone.error}
                mode="outline"
                .action=${async e => {
                  e.preventDefault();
                  e.stopPropagation();

                  await BHPromptSuspendUser.showPopup({
                    props: { user: instance.user, institutionID: this.institutionID.value },
                    modal: true,
                  });
                  this.requestUpdate();
                }}
              >
                <tr-tooltip .text=${html` <x-text>${this._translation.translate('suspend')}</x-text> `}> </tr-tooltip>
              </tr-button-small>
            `}
        <tr-button-small
          .color=${this.colors.tone.error}
          icon="delete"
          .action=${async () => {
            const confirmed = await XDialog.confirm(
              (this._translation.translate('user', 'participations_del') as string) + ` (${instance.user.email})`,
            );

            if (!confirmed || !this.institutionID.value) return;

            await removeParticipationsByUserAndInstitution(instance.user._id, this.institutionID.value)();

            this.requestUpdate();
          }}
          .color=${this.colors.secondary.shade(200)}
        ></tr-button-small>
      </x-flex>
    `;
  }
}
